// memoriaInstalacionPwa.ts — qué decidió esta persona, en ESTE teléfono, sobre el aviso para instalar la app (round 2026-10-29).
//
// La memoria es por DISPOSITIVO y no por usuario: instalar la app es algo que se hace en el teléfono, no en la cuenta
// (la misma aseadora puede entrar desde su celular y desde el de su pareja). Por eso vive en `localStorage` y no en la API.
//
//   «Ahora no» (o cerrar el aviso)      → no vuelve a salir por 14 días.
//   «No volver a mostrar»               → permanente.
//   «Ya la instalé» (iPhone)            → permanente.
//   Instalación aceptada / `appinstalled` → permanente.
//
// Reglas de robustez (el aviso es un extra: jamás debe romper la app ni insistir):
//   - El almacenamiento puede lanzar al ACCEDER (modo privado, datos de sitio bloqueados: `window.localStorage` mismo
//     lanza SecurityError) y al ESCRIBIR (cuota, Safari antiguo en modo privado). Todo va en try/catch.
//   - Si no se puede escribir, la decisión igual se respeta mientras la página siga abierta (copia en memoria): si «No
//     volver a mostrar» se ignorara por no poder guardarlo, el aviso reaparecería en la siguiente pantalla.
//   - Un valor corrupto o de otra versión se trata como «sin memoria». La clave lleva versión (`v1`): si algún día cambia
//     el significado de lo guardado, se sube a `v2` y todos vuelven a empezar sin migrar nada.
//   - «Una vez por sesión» usa `sessionStorage` con el mismo respaldo en memoria.
//
// El tiempo es un instante absoluto (`Date.now()`), no una fecha de negocio: los 14 días de espera no dependen de la
// zona horaria, así que aquí NO aplica `utils/businessDate`.

export const CLAVE_MEMORIA_PWA = "af.pwaInstalar.v1";
export const CLAVE_SESION_PWA = "af.pwaInstalar.v1.sesion";
export const DIAS_AHORA_NO = 14;
const MS_DIA = 24 * 60 * 60 * 1000;

export type MotivoSilencio = "no-mostrar" | "ya-instalada" | "instalada";

interface Memoria {
  v: 1;
  /** Instante hasta el cual no se muestra (epoch ms). */
  hasta?: number;
  /** Presente = no se vuelve a mostrar nunca en este dispositivo. */
  permanente?: MotivoSilencio;
}

// Respaldo cuando el almacenamiento no está disponible o no deja escribir.
let respaldoMemoria: Memoria | null = null;
let respaldoSesion = false;

function almacen(tipo: "local" | "sesion"): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return tipo === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function leerMemoria(): Memoria {
  let guardada: Memoria | null = null;
  try {
    const crudo = almacen("local")?.getItem(CLAVE_MEMORIA_PWA);
    if (crudo) {
      const dato = JSON.parse(crudo) as Partial<Memoria> | null;
      if (dato && typeof dato === "object" && dato.v === 1) guardada = dato as Memoria;
    }
  } catch {
    // JSON corrupto o almacenamiento que lanza: se sigue con el respaldo.
  }
  // Lo más restrictivo de las dos fuentes: lo guardado en disco y lo decidido en esta página.
  const a = guardada ?? { v: 1 as const };
  const b = respaldoMemoria ?? { v: 1 as const };
  return {
    v: 1,
    permanente: a.permanente ?? b.permanente,
    hasta: Math.max(a.hasta ?? 0, b.hasta ?? 0) || undefined,
  };
}

function guardarMemoria(m: Memoria): void {
  respaldoMemoria = m;
  try {
    almacen("local")?.setItem(CLAVE_MEMORIA_PWA, JSON.stringify(m));
  } catch {
    // Sin poder escribir: queda la copia en memoria de esta página.
  }
}

/** ¿Esta persona ya decidió (o instaló) y el aviso no debe salir ahora? */
export function avisoSilenciado(ahora: number = Date.now()): boolean {
  const m = leerMemoria();
  if (m.permanente) return true;
  if (typeof m.hasta === "number" && Number.isFinite(m.hasta)) {
    // Un `hasta` más lejos que el plazo máximo viene de un reloj mal puesto (o de un valor manipulado): se ignora en vez
    // de dejar el aviso apagado por años.
    if (m.hasta - ahora > DIAS_AHORA_NO * MS_DIA) return false;
    return ahora < m.hasta;
  }
  return false;
}

/** «Ahora no»: 14 días sin aviso. No pisa un silencio permanente. */
export function posponerAviso(ahora: number = Date.now()): void {
  const m = leerMemoria();
  guardarMemoria({ ...m, hasta: ahora + DIAS_AHORA_NO * MS_DIA });
}

/** «No volver a mostrar», «Ya la instalé» o instalación aceptada. */
export function silenciarParaSiempre(motivo: MotivoSilencio): void {
  const m = leerMemoria();
  guardarMemoria({ ...m, permanente: motivo });
}

/** ¿Ya se mostró el aviso en esta sesión del navegador (pestaña)? */
export function sesionYaMostrada(): boolean {
  try {
    if (almacen("sesion")?.getItem(CLAVE_SESION_PWA) === "1") return true;
  } catch {
    // se usa el respaldo
  }
  return respaldoSesion;
}

export function marcarSesionMostrada(): void {
  respaldoSesion = true;
  try {
    almacen("sesion")?.setItem(CLAVE_SESION_PWA, "1");
  } catch {
    // sin sessionStorage: basta con el respaldo en memoria
  }
}

/** Solo para pruebas: borra el estado en memoria de este módulo (lo guardado en el navegador se limpia aparte). */
export function reiniciarMemoriaPwaParaPruebas(): void {
  respaldoMemoria = null;
  respaldoSesion = false;
}
